import React, { Fragment, useContext, useState, useEffect, ReactElement } from "react";
import {
  FiUser,
  FiLogIn,
  FiChevronRight,
  FiAlertTriangle,
  FiLogOut,
  FiCheckSquare,
  FiSquare,
  FiHome,
  FiVideo,
  FiSend,
  FiNavigation,
  FiKey
} from "react-icons/fi";
import { IoMdCar } from "react-icons/io";
import { useTheme, useMediaQuery } from "@mui/material";
import { FaShower, FaStar, FaWheelchair } from "react-icons/fa";
import CarCrashIcon from "@mui/icons-material/CarCrash";
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import { Link } from "react-router-dom";
import { AssetLocationTypes, Booking, BookingStatus, CollectionDeliveryStatus, FuelType, LuggageType, SystemProfile, VehicleStatus } from "../../../../logic/api";

import styles from "./styles";
import flex from "../../../../styles/flex";

import ListViewItemCard from "../../../cards/ListItemCard/ListItemCard";

import { SessionContext } from "../../../../utility/useSession";
import { useDate } from "../../../../utility/useDate";
import { ReleaseFlag, useReleaseManagement } from "../../../../utility/feature-management";
import { useTranslation } from "react-i18next";
import usePaths from "@/utility/paths";
import { OperationalTimer } from "./OperationalTimer";
import { getBookingCardValues } from "./getBookingCardValues";
import BookingReferenceLine from "./BookingReferenceLine";

const LUGGAGE_TAG_KEY: Record<LuggageType, string> = {
  [LuggageType.Hand]: "booking.luggageTags.hand",
  [LuggageType.Hold]: "booking.luggageTags.hold",
  [LuggageType.Oversize]: "booking.luggageTags.oversize",
};

const BookingListItem: React.FunctionComponent<{
  booking: Booking;
  warning?: string;
  selected?: boolean;
  onClick?: () => void;
  onSelectionChanged?: (selected: boolean) => void;
  openInNewTab?: boolean;
  meetGreetCollectionDeliveryEnabled?: boolean;
}> = (props) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const paths = usePaths();
  const isLargeDevice = useMediaQuery(theme.breakpoints.up("md"));
  const [selected, setSelected] = useState(false);
  const { toFormattedDateString, toFormattedTimeString } = useDate();
  const card = getBookingCardValues(props.booking);

  const deprecateArchiveReleased = useReleaseManagement(ReleaseFlag.releaseDeprecateArchiveStatus);
  const showAnprOnBookingCard = useReleaseManagement(ReleaseFlag.releaseShowAnprOnBookingCard);
  const bookingCardIdentifiersReleased = useReleaseManagement(ReleaseFlag.releaseBookingCardIdentifiers);
  const isStoreFuelTypeReleased = useReleaseManagement(ReleaseFlag.releaseStoreFuelType);
  const isCollectionDeliveryReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreet);
  const isMeetGreetArrivalsReleased = useReleaseManagement(ReleaseFlag.releaseMeetGreetArrivals);
  const isAddLuggageFieldToBookingReleased = useReleaseManagement(ReleaseFlag.releaseAddLuggageFieldToBooking);

  let system_profile: SystemProfile = SystemProfile.airportParking;
  let useFirstName = false;

  function getVehicleStatusKey(statusId: VehicleStatus) {
    switch (statusId) {
      case VehicleStatus.NotCheckedIn:
        return 'booking.vehicleStatus.notCheckedIn';
      case VehicleStatus.PreCheckIn:
        return 'booking.vehicleStatus.preCheckIn';
      case VehicleStatus.CheckedIn:
        return 'booking.vehicleStatus.checkedIn';
      case VehicleStatus.CheckedOut:
        return 'booking.vehicleStatus.checkedOut';
      default:
        return 'booking.vehicleStatus.unknown';
    }
  }

  const statusKey = getVehicleStatusKey(card.vehicleStatusId);
  const translatedStatus = t(statusKey)

  if (session != null) {
    if (session.current != null) {
      if (session.current.site != null) {
        if (session.current.site.preferences.system_profile != null) {
          system_profile = session.current.site.preferences.system_profile;
          useFirstName = session.current.site.preferences.booking_leadContact_firstName_enabled;
        }
      }
    }
  }

  const border = styles.bookingListItemBorder(
    card.vehicleStatusId,
    card.statusId
  );

  useEffect(() => {
    if (props.selected != selected && props.onSelectionChanged != null) {
      setSelected(props.selected == true);
    }
  }, [props.selected]);

  if (session.current.site == null) {
    return <Fragment></Fragment>;
  }

  const getVehicleIcon = () => {
    if (card.hasVehicleIssues) {
      return <CarCrashIcon style={{ ...styles.icon, color: "rgb(235, 48, 79)" }} />
    }

    if (card.valet) {
      return <FaShower title="Valet" style={{ ...styles.icon, color: "#73B9FF" }} />
    }

    if (isStoreFuelTypeReleased && card.vehicleFuelType === FuelType.Electric) {
      return <ElectricCarIcon titleAccess="Electric" style={{ ...styles.icon, color: "#319C44" }} />;
    }

    return <IoMdCar style={styles.icon} />
  }

  const collectionDeliveryStatus = card.collectionDeliveryStatus;
  const useMeetGreetCollectionDeliveryCards =
    props.meetGreetCollectionDeliveryEnabled === true &&
    system_profile === SystemProfile.airportParking;
  const isCheckedIn = card.vehicleStatusId === VehicleStatus.CheckedIn;
  const isAwaitingCheckIn = [VehicleStatus.NotCheckedIn, VehicleStatus.PreCheckIn].includes(card.vehicleStatusId);
  const useMeetGreetBookingCard =
    useMeetGreetCollectionDeliveryCards && (isCheckedIn || isAwaitingCheckIn);
  const showBookingReferenceLine =
    bookingCardIdentifiersReleased &&
    useMeetGreetBookingCard &&
    isCheckedIn;
  const showCollectionOperation =
    useMeetGreetCollectionDeliveryCards &&
    card.collection === true &&
    (isCheckedIn
      ? collectionDeliveryStatus != null &&
      [CollectionDeliveryStatus.CollectionRequested, CollectionDeliveryStatus.CollectionInProgress].includes(collectionDeliveryStatus)
      : isAwaitingCheckIn);
  let operationDate = isCheckedIn
    ? card.expectedReturnDate ? new Date(card.expectedReturnDate) : null
    : card.bookingStartDate ? new Date(card.bookingStartDate) : null;
  let operationFlightNumber = card.returnFlightNumber ?? "";
  const hasFlightLandingTime = isCheckedIn && Boolean(props.booking.returnFlight?.flightDate);
  const hasReturnFlightInfo = hasFlightLandingTime || Boolean(operationFlightNumber);
  const luggageTagKey = isAddLuggageFieldToBookingReleased && hasReturnFlightInfo && props.booking.luggage != null
    ? LUGGAGE_TAG_KEY[props.booking.luggage]
    : undefined;
  const luggageTag = luggageTagKey ? t(luggageTagKey) : "";
  let operationIcon: ReactElement = isCheckedIn
    ? <FiLogOut aria-label="Delivery" style={{ ...styles.icon, color: "currentColor" }} />
    : <FiLogIn aria-label="Entry" style={{ ...styles.icon, color: "currentColor" }} />;
  let operationIconColour = theme.palette.text.primary;
  let timerStart: Date | string | null | undefined;

  if (showCollectionOperation) {
    operationIcon = <FiNavigation aria-label="Collection status" style={{ ...styles.icon, color: "currentColor" }} />;
    operationFlightNumber = "";

    switch (collectionDeliveryStatus) {
      case CollectionDeliveryStatus.CollectionRequested:
        operationIconColour = theme.palette.error.main;
        timerStart = card.collectionRequestedAt;
        operationDate = card.confirmedArrivalDate ? new Date(card.confirmedArrivalDate) : null;
        break;
      case CollectionDeliveryStatus.CollectionInProgress:
        operationIconColour = theme.palette.success.main;
        timerStart = card.vehicleCheckInDate;
        operationDate = card.confirmedArrivalDate ? new Date(card.confirmedArrivalDate) : null;
        break;
      case CollectionDeliveryStatus.CollectionComplete:
        operationIconColour = theme.palette.success.main;
        timerStart = card.collectionCompletedAt;
        operationDate = card.confirmedArrivalDate ? new Date(card.confirmedArrivalDate) : null;
        break;
      default:
        operationDate = new Date(card.outboundFlightDate ?? card.bookingStartDate);
    }
  } else if (card.delivery) {
    operationIcon = <FiSend aria-label="Delivery status" style={{ ...styles.icon, color: "currentColor" }} />;

    switch (collectionDeliveryStatus) {
      case CollectionDeliveryStatus.CollectionComplete:
        break;
      case CollectionDeliveryStatus.DeliveryRequested:
      case CollectionDeliveryStatus.DeliveryInProgress:
        operationIconColour = theme.palette.error.main;
        timerStart = card.deliveryRequestedAt;
        break;
      case CollectionDeliveryStatus.DeliveryComplete:
        operationIconColour = theme.palette.success.main;
        timerStart = card.deliveryCompletedAt;
        break;
    }
  }

  const operationalIcon = (
    <div style={{ display: "grid", justifyItems: "center", color: operationIconColour, fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
      {operationIcon}
      {timerStart ? <OperationalTimer startTime={timerStart} /> : null}
    </div>
  );

  const getERTIcon = () => {
    if (isCollectionDeliveryReleased && card.collectionDeliveryStatus === CollectionDeliveryStatus.DeliveryRequested) {
      const keyLocationType = card.keyLocationType;
      if (keyLocationType !== undefined) {
        const withDriver = [AssetLocationTypes.User, AssetLocationTypes.Users].includes(keyLocationType);
        return <FiSend style={{ ...styles.icon, color: withDriver ? "green" : "red" }} />;
      }
    }

    if (card.delivery) {
      return <IoMdCar title={t("lists.delivery")} style={{ ...styles.icon, color: "blue" }} />;
    }
    return <FiLogOut style={styles.icon} />;
  }

  const getArrivalsBookingListItem = (): ReactElement => {
    if (card.vehicleStatusId === VehicleStatus.CheckedIn && card.collectionDeliveryStatus && [CollectionDeliveryStatus.CollectionRequested, CollectionDeliveryStatus.CollectionInProgress].includes(card.collectionDeliveryStatus)) {
      return <ListViewItemCard
        icon={<FiNavigation style={{ ...styles.icon, color: "green" }} />}
        text={toFormattedDateString(new Date(card.vehicleCheckInDate))}
        subText={toFormattedTimeString(new Date(card.vehicleCheckInDate))}
      />
    }

    if (card.collectionDeliveryStatus === CollectionDeliveryStatus.CollectionRequested && card.confirmedArrivalDate) {
      return <ListViewItemCard
        icon={<FiNavigation style={{ ...styles.icon, color: "red" }} />}
        text={toFormattedDateString(new Date(card.confirmedArrivalDate))}
        subText={toFormattedTimeString(new Date(card.confirmedArrivalDate))}
      />
    }

    if (card.collection) {
      return <ListViewItemCard
        icon={<IoMdCar title={t("lists.collection")} style={{ ...styles.icon, color: "brown" }} />}
        text={toFormattedDateString(new Date(card.outboundFlightDate ?? card.bookingStartDate))}
        subText={toFormattedTimeString(new Date(card.outboundFlightDate ?? card.bookingStartDate))}
      />
    }

    return <ListViewItemCard
      icon={<FiLogIn style={styles.icon} />}
      text={toFormattedDateString(new Date(card.bookingStartDate))}
      subText={toFormattedTimeString(new Date(card.bookingStartDate))}
    />
  }

  const renderANPRInfo = () => {
    if (!showAnprOnBookingCard || card.vehicleStatusId !== VehicleStatus.NotCheckedIn) {
      return <></>
    }

    const firstScan = card.firstAnprScanDateTime;
    if (firstScan) {
      return <ListViewItemCard
        icon={<FiVideo title={t("bookings.anprTimestamp")} style={{ ...styles.icon }} />}
        text={toFormattedDateString(firstScan)}
        subText={toFormattedTimeString(firstScan)}
      />
    } else {
      return <div style={{ display: "flex", flex: "1 1 100%" }} />
    }
  }

  return (
    <div style={{ position: "relative" }}>
      {props.onSelectionChanged != null && (
        <div onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelected(!selected);
          if (props.onSelectionChanged != null) {
            props.onSelectionChanged(!selected);
          }

          return false;
        }} style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 80, zIndex: 999 }}></div>)}

      <Link
        to={paths.booking(props.booking.id)}
        target={props.openInNewTab ? "_blank" : "_self"}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'block',
          ...styles.bookingListItem,
          ...border,
          paddingBottom: props.warning != null ? 0 : 20,
          backgroundColor: selected == true ? "lightblue" : "white",
        }}
        onClick={() => {
          if (!props.openInNewTab && props.onClick) {
            props.onClick();
          }
        }}
      >
        <div
          style={{
            position: "absolute",
            textAlign: "center",
            width: 120,
            left: -67,
            bottom: 0,
            height: 15,
            top: 30,
            fontSize: 10,
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "white",
            transform: "rotate(270deg)",
          }}
        >
          <span>
            {card.statusId == BookingStatus.Cancelled ? (
              <Fragment>{deprecateArchiveReleased ? t("general.cancelled") : t("general.archived")}</Fragment>
            ) : (
              <Fragment>
                {translatedStatus}
              </Fragment>
            )}
          </span>
        </div>
        <div style={styles.bookingListItemFlex}>
          {system_profile == SystemProfile.fleetManagement && (
            <Fragment>

              {(isLargeDevice == true &&
                card.vehicleStatusId == VehicleStatus.CheckedIn) && (
                  <ListViewItemCard
                    icon={<FiHome style={styles.icon} />}
                    text={card.vehicleLocationPresent ? card.vehicleLocationName : t("lists.noLocation")}
                    subText={card.vehicleLocationPresent ? card.vehicleLocationParentName : t("lists.noLocation")}

                  />
                )}
            </Fragment>
          )}

          {isLargeDevice == true && (
            <ListViewItemCard
              icon={system_profile == SystemProfile.fleetManagement ? null : (card.vip ? <FaStar title={t("booking.labels.vip")} style={{ ...styles.icon, color: "orange" }} /> : <FiUser style={styles.icon} />)}
              text={system_profile == SystemProfile.fleetManagement ? (card.altRef ?? t("lists.noBarcode")) : (useFirstName ? `${card.fullName}` : `${card.shortName}`)}
              subText={system_profile == SystemProfile.fleetManagement
                ? `${card.shortName}`
                : useMeetGreetBookingCard && isCheckedIn
                  ? (showBookingReferenceLine
                    ? <BookingReferenceLine bookingReference={card.ref} trackingId={props.booking.trackingId?.trim()} />
                    : card.bookingReference)
                  : `${card.ref}`}
              subSubText={system_profile == SystemProfile.fleetManagement
                ? ""
                : useMeetGreetBookingCard
                  ? card.packageReference
                  : `${card.legacyPackageReference}`}
              subSubTextColor="grey"
              shrinkable={showBookingReferenceLine}
            />
          )}

          <ListViewItemCard
            icon={getVehicleIcon()}
            text={card.vehicleRegistration}
            subText={card.vehicleDescription}
            subSubText={card.vehicleColour}
          />

          {renderANPRInfo()}

          {useMeetGreetBookingCard && isCheckedIn && isLargeDevice ? (
            <ListViewItemCard
              icon={card.keyLocationPresent ? <FiKey aria-label="Key location" style={styles.icon} /> : null}
              text={card.keyLocationParentName ?? ""}
              subText={card.keyLocationName ?? ""}
            />
          ) : null}

          {useMeetGreetBookingCard ? (
            <ListViewItemCard
              icon={operationalIcon}
              text={operationDate ? toFormattedDateString(operationDate) : ""}
              subText={operationDate ? toFormattedTimeString(operationDate) : ""}
              subSubText={
                luggageTag
                  ? (operationFlightNumber ? `${operationFlightNumber}, ${luggageTag}` : luggageTag)
                  : operationFlightNumber
              }
            />
          ) : null}

          {!useMeetGreetBookingCard ? (
            (isMeetGreetArrivalsReleased && isLargeDevice == true &&
              (card.vehicleStatusId == VehicleStatus.NotCheckedIn ||
                card.vehicleStatusId == VehicleStatus.PreCheckIn ||
                (card.vehicleStatusId === VehicleStatus.CheckedIn && card.collectionDeliveryStatus && [CollectionDeliveryStatus.CollectionRequested, CollectionDeliveryStatus.CollectionInProgress].includes(card.collectionDeliveryStatus)))) ? (
              getArrivalsBookingListItem()
            ) : (isLargeDevice == true &&
              (card.vehicleStatusId == VehicleStatus.NotCheckedIn ||
                card.vehicleStatusId == VehicleStatus.PreCheckIn)) &&
              session.current.site.preferences.booking_outboundFlight_enabled ==
              true &&
              (card.outboundFlightDate || "").length > 0 ? (
              <ListViewItemCard
                icon={card.collection ? <IoMdCar title={t("lists.collection")} style={{ ...styles.icon, color: "brown" }} /> : <FiLogIn style={styles.icon} />}
                text={
                  card.outboundFlightDate
                    ? toFormattedDateString(new Date(card.outboundFlightDate))
                    : ""
                }
                subText={
                  card.outboundFlightDate
                    ? toFormattedTimeString(new Date(card.outboundFlightDate))
                    : ""
                }
              />
            ) : (
              <ListViewItemCard
                icon={card.collection ? <IoMdCar title={t("lists.collection")} style={{ ...styles.icon, color: "brown" }} /> : <FiLogIn style={styles.icon} />}
                text={toFormattedDateString(new Date(card.bookingStartDate))}
                subText={toFormattedTimeString(new Date(card.bookingStartDate))}
              />
            )
          ) : null}

          {!useMeetGreetBookingCard && system_profile != SystemProfile.fleetManagement ? (
            isLargeDevice == true && card.vehicleStatusId == VehicleStatus.CheckedIn ? (
              <ListViewItemCard
                icon={getERTIcon()}
                text={toFormattedDateString(new Date(card.expectedReturnDate))}
                subText={toFormattedTimeString(new Date(card.expectedReturnDate))}
              />
            ) : null
          ) : null}

          <div style={flex.flexAuto}>
            <div
              style={{ ...flex.flexRows, alignItems: "center", height: "100%" }}
            >
              <div style={flex.flexAuto}>
                {card.assistance == true ? <FaWheelchair style={{ ...styles.iconChevron, color: "rgb(235, 48, 79)" }} /> : <FiChevronRight style={styles.iconChevron} />}
              </div>
            </div>
          </div>

          {props.onSelectionChanged != null && (

            <div style={flex.flexAuto}>
              <div
                style={{ ...flex.flexRows, alignItems: "center", height: "100%" }}
              >
                <div style={flex.flexAuto} >
                  {selected == true ? <FiCheckSquare style={styles.iconChevron} /> : <FiSquare style={styles.iconChevron} />}
                </div>
              </div>
            </div>
          )}
        </div>
        {props.warning != null && (
          <div style={{ color: "red", textAlign: "right", padding: 5 }}>
            <FiAlertTriangle></FiAlertTriangle> <b>{props.warning}</b>
          </div>

        )}

      </Link>
    </div>

  );
};

export default BookingListItem;
